/**
 * /auth/verify Landing-Page — wird vom Magic-Link-Button/-Link angesteuert.
 *
 * Flow:
 *   1. Query-Params `token` und `email` lesen.
 *   2. /api/auth/verify?json=1 aufrufen → Cookie wird vom Worker gesetzt.
 *   3. AuthContext refreshen, Redirect auf /dashboard.
 *
 * Fehlerfälle (Token abgelaufen, schon verwendet, ungültig) werden als
 * 400 zurückgegeben — wir zeigen die Server-Message an und bieten den Link
 * zurück zum Login.
 */

import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ApiError, verifyMagicLink } from "../lib/api";
import { useAuth } from "../lib/AuthContext";

export default function AuthVerify() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [status, setStatus] = useState<"loading" | "ok" | "error">("loading");
  const [error, setError] = useState<string | null>(null);
  const ran = useRef(false);

  useEffect(() => {
    // StrictMode in dev würde den Effect zweimal feuern; der zweite Call würde
    // am serverseitigen `used_at`-Guard scheitern. Einmal reicht.
    if (ran.current) return;
    ran.current = true;

    const token = params.get("token");
    const email = params.get("email");
    if (!token || !email) {
      setStatus("error");
      setError("Ungültiger Login-Link (token oder email fehlen).");
      return;
    }

    (async () => {
      try {
        await verifyMagicLink(token, email);
        await refresh();
        setStatus("ok");
        setTimeout(() => nav("/dashboard", { replace: true }), 400);
      } catch (err) {
        setStatus("error");
        setError(err instanceof ApiError ? err.message : String(err));
      }
    })();
  }, [params, refresh, nav]);

  return (
    <div className="max-w-md mx-auto py-20 text-center">
      {status === "loading" && (
        <>
          <div className="animate-spin w-10 h-10 border-2 border-court-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-slate-400">Login wird geprüft…</p>
        </>
      )}
      {status === "ok" && (
        <>
          <div className="w-10 h-10 rounded-full bg-emerald-600 flex items-center justify-center mx-auto mb-4 text-xl">
            ✓
          </div>
          <p className="text-emerald-300">Erfolgreich eingeloggt. Weiterleitung…</p>
        </>
      )}
      {status === "error" && (
        <>
          <div className="w-10 h-10 rounded-full bg-red-600/70 flex items-center justify-center mx-auto mb-4 text-xl">
            !
          </div>
          <p className="text-red-300 mb-2">Login fehlgeschlagen</p>
          <p className="text-sm text-slate-400 mb-6">{error}</p>
          <Link
            to="/login"
            className="inline-block rounded-lg bg-slate-800 hover:bg-slate-700 px-4 py-2 text-sm transition"
          >
            Neuen Login anfordern
          </Link>
        </>
      )}
    </div>
  );
}
